import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@cbhq/cds-web/tables';
import { TextTitle3, TextTitle4, TextBody } from '@cbhq/cds-web/typography';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { useRouter } from 'next/navigation';
import { PortfolioPositionsEmptyState } from '@/components/Portfolio/PortfolioPositionsEmptyState';
import { usePositions } from '@/onchain/hooks/usePositions';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { ERROR_MESSAGE_PORTFOLIO_POSITIONS_FAILED } from '@/app/utils/errors';
import { DisplayType, format } from '@/utils/numberFormatter';
import { PortfolioPositionMobileRow } from './PortfolioPositionMobileRow';
import { PortfolioPositionDesktopRow } from './PortfolioPositionDesktopRow';

const PAGE_SIZE = 10;

// Multiple fallbacks component
const LoadingFallbacks = memo(function LoadingFallbacks() {
  return (
    <VStack gap={2} testID="portfolio-positions-loading">
      {Array.from({ length: PAGE_SIZE }, (_, i) => (
        <Fallback
          key={`fallback-position-${i}`}
          width="100%"
          height={92}
          shape="squircle"
          testID={`portfolio-positions-loading-${i}`}
        />
      ))}
    </VStack>
  );
});

function PortfolioPositionsList() {
  const { isPhone, isPhoneLandscape } = useBreakpoints();
  const router = useRouter();
  const showMobileLayout = useMemo(
    () => Boolean(isPhone || isPhoneLandscape),
    [isPhone, isPhoneLandscape],
  );

  const [currentPage, setCurrentPage] = useState(0);

  const { positions, pagination, isPending, isFetching } = usePositions({
    page: currentPage,
    pageSize: PAGE_SIZE,
  });

  const handlePositionSelect = useCallback(
    (positionId: bigint, positionManagerAddress?: `0x${string}`) => {
      const params = new URLSearchParams({ positionId: positionId.toString() });
      if (positionManagerAddress) {
        params.set('positionManagerAddress', positionManagerAddress);
      }
      router.push(`/positions?${params.toString()}`);
    },
    [router],
  );

  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    if (pagination.hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [pagination.hasMore]);

  if (positions.length === 0 && !isPending && !isFetching) {
    return <PortfolioPositionsEmptyState />;
  }

  // Render the previous page button if we're not on the first page
  const renderPrevButton = () => {
    if (currentPage === 0) {
      return <HStack width={100} />; // Empty spacer with similar width
    }

    return (
      <Button
        disabled={isPending}
        onClick={handlePrevPage}
        testID="portfolio-pagination-prev"
        transparent
        compact
        variant="secondary"
      >
        Previous
      </Button>
    );
  };

  // Render the next page button if there are more pages
  const renderNextButton = () => {
    if (!pagination.hasMore) {
      return <HStack width={100} />; // Empty spacer with similar width
    }

    return (
      <Button
        disabled={isPending}
        onClick={handleNextPage}
        testID="portfolio-pagination-next"
        transparent
        compact
        variant="secondary"
      >
        Next
      </Button>
    );
  };

  return (
    <VStack paddingX={1}>
      <VStack gap={1} testID="portfolio-positions-list">
        <Table>
          {!showMobileLayout && (
            <TableHeader sticky>
              <TableRow>
                <TableCell title="Position" width="30%" noWrap />
                <TableCell title="Fees" width="12%" noWrap />
                <TableCell title="Liquidity" width="12%" noWrap />
                <TableCell title="Range" width="30%" noWrap />
                <TableCell title="Status" width="16%" noWrap />
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            {positions.map((position) =>
              showMobileLayout ? (
                <PortfolioPositionMobileRow
                  key={position.positionId.toString()}
                  position={position}
                  testId={`position-${position.positionId}`}
                  onSelect={handlePositionSelect}
                />
              ) : (
                <PortfolioPositionDesktopRow
                  key={position.positionId.toString()}
                  position={position}
                  testId={`position-${position.positionId}`}
                  onSelect={handlePositionSelect}
                />
              ),
            )}
          </TableBody>
        </Table>

        {isPending && <LoadingFallbacks />}
      </VStack>

      {pagination.totalPages > 1 && (
        <HStack
          justifyContent="space-between"
          alignItems="center"
          testID="portfolio-pagination-controls"
        >
          {renderPrevButton()}

          <HStack gap={2} alignItems="center">
            <TextBody as="span" testID="portfolio-pagination-current-page">
              {currentPage + 1}
            </TextBody>
            <TextBody as="span">of</TextBody>
            <TextBody as="span" testID="portfolio-pagination-total">
              {pagination.totalPages}
            </TextBody>
          </HStack>

          {renderNextButton()}
        </HStack>
      )}
    </VStack>
  );
}

const PortfolioPositionsHeader = memo(function PortfolioPositionsHeader() {
  const { positions: allPositions } = usePositions();
  const totalPositionsValue = useMemo(
    () => (allPositions ?? []).reduce((acc, curr) => acc + (curr.totalLiquidityNotional ?? 0), 0),
    [allPositions],
  );

  return (
    <HStack paddingX={3} alignItems="baseline" gap={1}>
      <TextTitle3 as="span">Positions</TextTitle3>
      <TextTitle4 as="span" color="fgMuted">
        {format({ value: totalPositionsValue, type: DisplayType.Fiat })}
      </TextTitle4>
    </HStack>
  );
});

const PortfolioPositionsContent = memo(function PortfolioPositionsContent() {
  const account = useAccount();

  if (!account.address) {
    return <PortfolioPositionsEmptyState />;
  }

  return (
    <VStack gap={2}>
      <Suspense fallback={<LoadingFallbacks />}>
        <PortfolioPositionsHeader />
        <PortfolioPositionsList />
      </Suspense>
    </VStack>
  );
});

const PortfolioPositionsWithErrorBoundary = memo(function PortfolioPositionsWithErrorBoundary() {
  return (
    <VStack background="bg" borderRadius={300} paddingY={3} gap={2}>
      <ErrorBoundary message={ERROR_MESSAGE_PORTFOLIO_POSITIONS_FAILED}>
        <PortfolioPositionsContent />
      </ErrorBoundary>
    </VStack>
  );
});

export const PortfolioPositions = memo(function PortfolioPositions() {
  return <PortfolioPositionsWithErrorBoundary />;
});
